import React from 'react';
import { Card } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Award, BookOpen, Users, Linkedin } from 'lucide-react';

const instructors = [
  {
    name: "Dr. Robert Chen",
    role: "Lead Safety Instructor",
    credentials: "CSP, CIH, OSHA Authorized",
    bio: "20+ years in occupational safety with experience at Fortune 500 companies. Former OSHA compliance officer.",
    image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop&crop=face",
    stats: { courses: 50, students: 12000 }
  },
  {
    name: "Maria Santos",
    role: "PPE & Hazard Specialist",
    credentials: "OHST, CHST, ASP",
    bio: "Industrial hygiene expert specializing in chemical safety and PPE selection. Published author on workplace hazards.",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&h=200&fit=crop&crop=face",
    stats: { courses: 35, students: 8500 }
  },
  {
    name: "James Mitchell",
    role: "Emergency Response Expert",
    credentials: "EMT-P, CFPS, ARM",
    bio: "Former fire department captain with 15 years of emergency response training. Certified fire protection specialist.",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=face",
    stats: { courses: 28, students: 6200 }
  }
];

const Instructors = () => {
  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-12">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Expert-Led Training</span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
            Learn From Industry Experts
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Our instructors bring decades of real-world safety experience to your training
          </p>
        </div>

        {/* Instructors grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {instructors.map((instructor, index) => (
            <Card 
              key={index} 
              className="p-6 bg-white border border-slate-200 rounded-2xl hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
            >
              {/* Avatar and name */}
              <div className="text-center mb-4">
                <Avatar className="w-24 h-24 mx-auto mb-4 border-4 border-emerald-100 group-hover:border-emerald-200 transition-colors">
                  <AvatarImage src={instructor.image} alt={instructor.name} className="object-cover" />
                  <AvatarFallback className="bg-emerald-500 text-white text-xl">
                    {instructor.name.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <h3 className="text-lg font-bold text-slate-900">{instructor.name}</h3>
                <p className="text-emerald-600 text-sm font-medium">{instructor.role}</p>
              </div>

              {/* Credentials */}
              <div className="flex flex-wrap justify-center gap-1 mb-4">
                {instructor.credentials.split(', ').map((cred, i) => (
                  <Badge key={i} variant="secondary" className="bg-slate-100 text-slate-600 text-xs">
                    {cred}
                  </Badge>
                ))}
              </div>

              {/* Bio */}
              <p className="text-slate-600 text-sm text-center mb-4 line-clamp-3">
                {instructor.bio}
              </p>

              {/* Stats */}
              <div className="flex justify-center gap-6 pt-4 border-t border-slate-100">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-emerald-600">
                    <BookOpen className="w-4 h-4" />
                    <span className="font-bold">{instructor.stats.courses}+</span>
                  </div>
                  <p className="text-xs text-slate-500">Courses</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-emerald-600">
                    <Users className="w-4 h-4" />
                    <span className="font-bold">{(instructor.stats.students / 1000).toFixed(1)}k</span>
                  </div>
                  <p className="text-xs text-slate-500">Students</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Credentials bar */}
        <div className="mt-12 text-center">
          <p className="text-slate-500 text-sm mb-4">Our instructors hold certifications from</p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            {['OSHA', 'BCSP', 'AIHA', 'NFPA', 'NSC'].map((org, i) => (
              <div key={i} className="flex items-center gap-2">
                <Award className="w-5 h-5 text-slate-400" />
                <span className="font-semibold text-slate-600">{org}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Instructors;
